import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Paper,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import NotificationsIcon from "@mui/icons-material/Notifications";
import api from "../../utils/api";

const AddWorkOrder = ({ open, handleClose, propertyId, onWorkOrderAdded }) => {
  const [createdBy, setCreatedBy] = useState(null);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifyMember, setNotifyMember] = useState(true);
  const [formConfig, setFormConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  const [formData, setFormData] = useState({
    description: "",
    details: "",
    property_id: propertyId || "",
    workorder_type_id: "",
    workorder_subtype_id: "",
    location_id: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const dialogCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.14)",
    backgroundColor: "rgba(255,255,255,0.98)",
  };

  const sectionCardSx = {
    p: 2.5,
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(255,255,255,0.98)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
  };

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setCreatedBy(parsedUser?.userId ?? null);
      } catch (e) {
        console.error("Failed to parse user from sessionStorage", e);
      }
    }
  }, []);

  // Re-fetch form config every time the dialog opens
  useEffect(() => {
    if (!open) return;
    api.get("/wo-form-config").then(r => {
      if (r.data && Object.keys(r.data).length > 0) {
        setFormConfig(r.data);
        // Reset notify to match config default (checkbox enabled = default true)
        setNotifyMember(r.data.fields?.notify_checkbox !== false);
      }
      setConfigLoaded(true);
    }).catch(() => { setConfigLoaded(true); }); // still mark loaded on error
  }, [open]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, property_id: propertyId || "" }));
  }, [propertyId]);

  useEffect(() => {
    const fetchTypes = async () => {
      if (!propertyId) return;
      try {
        const res = await api.get(`/workorder-types/by-member`, {
          params: { member_code: propertyId },
        });
        setWorkOrderTypes(res.data || []);
      } catch (err) {
        console.error("Failed to fetch work order types:", err);
      }
    };
    fetchTypes();
  }, [propertyId]);

  useEffect(() => {
    if (!formData.workorder_type_id) {
      setSubtypes([]);
      setLocations([]);
      return;
    }

    const fetchData = async () => {
      try {
        const [subRes, locRes] = await Promise.all([
          api.get(
            `/workorder-subtypes-by-type?type_id=${formData.workorder_type_id}`
          ),
          api.get(
            `/workorder-locations-by-type?type_id=${formData.workorder_type_id}`
          ),
        ]);
        setSubtypes(subRes.data || []);
        setLocations(locRes.data || []);
      } catch (err) {
        console.error("Failed to fetch subtypes/locations:", err);
        setSubtypes([]);
        setLocations([]);
      }
    };

    fetchData();
  }, [formData.workorder_type_id]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "workorder_type_id" && {
        workorder_subtype_id: "",
        location_id: "",
      }),
    }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    multiple: true,
  });

  const resetForm = () => {
    setFormData({
      description: "",
      details: "",
      property_id: propertyId || "",
      workorder_type_id: "",
      workorder_subtype_id: "",
      location_id: "",
    });
    setSelectedFiles([]);
    setSubtypes([]);
    setLocations([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const {
      description,
      details,
      property_id,
      workorder_type_id,
      workorder_subtype_id,
      location_id,
    } = formData;

    const cfg = formConfig?.fields || {};
    const needsSubtype  = cfg.subtype  !== false;
    const needsLocation = cfg.location !== false;

    if (
      !description ||
      (cfg.details !== false && !details) ||
      !property_id ||
      !workorder_type_id ||
      (needsSubtype  && !workorder_subtype_id) ||
      (needsLocation && !location_id)
    ) {
      setSnackbarMessage("Please fill in all required fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        description: description.trim(),
        details: details.trim(),
        property_id,
        workorder_type_id,
        workorder_subtype_id: workorder_subtype_id || null,
        location_id: location_id || null,
        created_by: createdBy,
        notify_member: notifyMember,
      };

      const res = await api.post(`/addWorkOrder`, payload);
      const workorder_id = res.data?.workOrderId;
      if (!workorder_id) throw new Error("Missing workOrderId from response");

      if (selectedFiles.length > 0) {
        const uploadData = new FormData();
        uploadData.append("workorder_id", workorder_id);
        uploadData.append("uploaded_by", createdBy);
        selectedFiles.forEach((file) => uploadData.append("upload_urls", file));

        await api.post("/workorder-files", uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setSnackbarMessage("Work order created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      resetForm();
      if (onWorkOrderAdded) onWorkOrderAdded();
      handleClose();
    } catch (err) {
      console.error("Error creating work order:", err);
      setSnackbarMessage(
        err.response?.data?.message || "Failed to create work order."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: dialogCardSx,
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2.5, md: 3 },
            pt: 3,
            pb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            {formConfig?.title || "Create New Work Order"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            {formConfig?.subtitle || "Submit a maintenance request for this property."}
          </Typography>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 2, md: 3 },
            py: 3,
            backgroundColor: "rgba(47,99,173,0.015)",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <Paper elevation={0} sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <BuildOutlinedIcon sx={{ color: "#2f63ad" }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                    {formConfig?.fields?.section_title || "Request Information"}
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="workorder-type-label">
                      Work Order Type
                    </InputLabel>
                    <Select
                      labelId="workorder-type-label"
                      name="workorder_type_id"
                      value={formData.workorder_type_id}
                      onChange={handleFormChange}
                      required
                      label={formConfig?.field_labels?.type        || "Work Order Type"}
                    >
                      {workOrderTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth disabled={!subtypes.length}>
                      <InputLabel id="subtype-label">Subtype</InputLabel>
                      <Select
                        labelId="subtype-label"
                        name="workorder_subtype_id"
                        value={formData.workorder_subtype_id}
                        onChange={handleFormChange}
                        required
                        label={formConfig?.field_labels?.subtype   || "Subtype"}
                      >
                        {subtypes.map((sub) => (
                          <MenuItem key={sub.id} value={sub.id}>
                            {sub.subtype_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth disabled={!locations.length}>
                      <InputLabel id="location-label">Location</InputLabel>
                      <Select
                        labelId="location-label"
                        name="location_id"
                        value={formData.location_id}
                        onChange={handleFormChange}
                        required
                        label={formConfig?.field_labels?.location  || "Location"}
                      >
                        {locations.map((loc) => (
                          <MenuItem key={loc.id} value={loc.id}>
                            {loc.location_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <TextField
                    fullWidth
                    label={formConfig?.field_labels?.description || "Description"}
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    required
                  />

                  <TextField
                    fullWidth
                    label={formConfig?.field_labels?.details   || "Details"}
                    name="details"
                    value={formData.details}
                    onChange={handleFormChange}
                    required
                    multiline
                    rows={4}
                  />

                  <TextField
                    fullWidth
                    label="Member ID"
                    name="property_id"
                    value={formData.property_id}
                    disabled
                    InputProps={{ readOnly: true, style: { backgroundColor: "#f8fafc", color: "#555" } }}
                  />

                  {/* Only render after config has loaded - prevents flash when config disables the checkbox */}
                  {configLoaded && formConfig?.fields?.notify_checkbox && (
                    <Box sx={{ px:0.5, py:0.5, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.03)", border:"1px solid rgba(47,99,173,0.08)" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={notifyMember}
                            onChange={e => setNotifyMember(e.target.checked)}
                            sx={{ color:"#2f63ad", "&.Mui-checked":{ color:"#2f63ad" } }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight:600, color:"#223142" }}>
                              {formConfig?.fields?.notify_label || "Please contact me back regarding this request"}
                            </Typography>
                            {!notifyMember && (
                              <Typography variant="caption" sx={{ color:"#6b778c" }}>
                                You will not receive email notifications for this request.
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  )}
                </Stack>
              </Paper>

              <Paper elevation={0} sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <AttachFileOutlinedIcon sx={{ color: "#2f63ad" }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#223142" }}
                  >
                    Attach Files
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                  Upload images, PDFs, or Word documents to support this request.
                </Typography>

                <Box
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    border: "2px dashed #90caf9",
                    borderRadius: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#f5faff",
                      borderColor: "#1976d2",
                    },
                    mb: 2,
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon
                    sx={{ fontSize: 40, color: "#1976d2", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {isDragActive
                      ? "Drop files here..."
                      : "Drag & drop multiple files here, or click to select"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ mt: 0.5 }}
                  >
                    Accepted: Images, PDFs, Word docs
                  </Typography>
                </Box>

                {selectedFiles.length > 0 && (
                  <Box
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                      p: 1,
                      maxHeight: 170,
                      overflowY: "auto",
                      backgroundColor: "#fff",
                    }}
                  >
                    {selectedFiles.map((file, idx) => (
                      <Box
                        key={idx}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          borderBottom:
                            idx !== selectedFiles.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                          py: 0.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: "80%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "#223142",
                          }}
                        >
                          {file.name}
                        </Typography>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFile(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Stack>

            <DialogActions
              sx={{
                px: 0,
                pt: 3,
                pb: 0,
                mt: 1,
                display: "flex",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? <CircularProgress size={18} color="inherit" /> : null
                }
                sx={{
                  backgroundColor: "#2f63ad",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#1f4f94",
                    boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                  },
                }}
              >
                {isSubmitting ? "Submitting..." : (formConfig?.submit_label || "Submit Work Order")}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddWorkOrder;
